"use client";

import { WikiArticle } from "@/lib/wikipedia";

interface ArticleCardProps {
  article: WikiArticle | null;
  isLoading: boolean;
}

/**
 * Formats Wikipedia article content by detecting section headers
 * Wikipedia API with exsectionformat=wiki returns headings like:
 * == Heading == (h2)
 * === Subheading === (h3)
 * ==== Sub-subheading ==== (h4)
 */
function formatArticleContent(extract: string | undefined) {
  if (!extract) {
    return <p className="text-gray-500 italic">No content available.</p>;
  }

  // Split content into lines for processing
  const lines = extract.split("\n");
  const elements: React.ReactNode[] = [];
  let currentParagraph: string[] = [];
  let elementIndex = 0;

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join("\n").trim();
      if (text) {
        elements.push(
          <p
            key={elementIndex++}
            className="mb-3 last:mb-0 whitespace-pre-line"
          >
            {text}
          </p>,
        );
      }
      currentParagraph = [];
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check for wiki-style headings: == Heading ==, === Subheading ===, etc.
    const headingMatch = trimmedLine.match(/^(={2,6})\s*(.+?)\s*\1$/);

    if (headingMatch) {
      // Flush any pending paragraph before the heading
      flushParagraph();

      const level = headingMatch[1].length; // Number of = signs (2-6)
      const headingText = headingMatch[2];

      // Style based on heading level
      // == is h2 (largest), === is h3, etc.
      if (level === 2) {
        elements.push(
          <h2
            key={elementIndex++}
            className="font-bold text-base sm:text-lg md:text-xl text-gray-900 mt-5 mb-2 first:mt-0 border-b border-gray-200 pb-1"
          >
            {headingText}
          </h2>,
        );
      } else if (level === 3) {
        elements.push(
          <h3
            key={elementIndex++}
            className="font-bold text-sm sm:text-base md:text-lg text-gray-800 mt-4 mb-2 first:mt-0"
          >
            {headingText}
          </h3>,
        );
      } else if (level === 4) {
        elements.push(
          <h4
            key={elementIndex++}
            className="font-semibold text-sm sm:text-base text-gray-700 mt-3 mb-1.5 first:mt-0"
          >
            {headingText}
          </h4>,
        );
      } else {
        // h5, h6 - smaller subheadings
        elements.push(
          <h5
            key={elementIndex++}
            className="font-semibold text-xs sm:text-sm text-gray-600 mt-2 mb-1 first:mt-0"
          >
            {headingText}
          </h5>,
        );
      }
    } else if (trimmedLine === "") {
      // Empty line - might be paragraph break
      flushParagraph();
    } else {
      // Regular text line
      currentParagraph.push(trimmedLine);
    }
  }

  // Flush any remaining paragraph
  flushParagraph();

  return elements;
}

export function ArticleCard({ article, isLoading }: ArticleCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 animate-pulse h-full">
        <div className="h-6 sm:h-8 bg-gray-200 rounded w-3/4 mb-3 sm:mb-4" />
        <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2 mb-4 sm:mb-6" />
        <div className="space-y-2 sm:space-y-3">
          <div className="h-3 sm:h-4 bg-gray-200 rounded" />
          <div className="h-3 sm:h-4 bg-gray-200 rounded" />
          <div className="h-3 sm:h-4 bg-gray-200 rounded w-5/6" />
          <div className="h-3 sm:h-4 bg-gray-200 rounded w-4/6" />
        </div>
        <div className="flex gap-2 mt-4 sm:mt-6">
          <div className="h-5 sm:h-6 w-16 sm:w-20 bg-gray-200 rounded-full" />
          <div className="h-5 sm:h-6 w-20 sm:w-24 bg-gray-200 rounded-full" />
          <div className="h-5 sm:h-6 w-14 sm:w-16 bg-gray-200 rounded-full" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 text-center h-full flex flex-col justify-center">
        <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🌍</div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
          Ready to Explore?
        </h2>
        <p className="text-sm sm:text-base text-gray-500">
          Click the button below to discover a random Wikipedia article!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden h-full flex flex-col">
      {/* Thumbnail */}
      {article.thumbnail && (
        <div className="h-32 sm:h-40 md:h-48 overflow-hidden flex-shrink-0">
          <img
            src={article.thumbnail}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4 sm:p-6 flex flex-col flex-1 overflow-hidden">
        {/* Title */}
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-1.5 sm:mb-2 flex-shrink-0 line-clamp-2">
          {article.title}
        </h2>

        {/* Description */}
        {article.description && (
          <p className="text-xs sm:text-sm text-[#141beb] font-medium mb-2 sm:mb-3 capitalize flex-shrink-0">
            {article.description}
          </p>
        )}

        {/* Categories */}
        {article.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 flex-shrink-0">
            {article.categories.map((category) => (
              <span
                key={category}
                className="px-2 py-0.5 bg-[#02eec4]/10 text-[#141beb] text-[10px] sm:text-xs font-medium rounded-full"
              >
                {category}
              </span>
            ))}
          </div>
        )}

        {/* Full Article Content - Scrollable */}
        <div className="flex-1 overflow-y-auto mb-3 sm:mb-4 pr-1 sm:pr-2 scrollbar-thin">
          <div className="text-gray-600 leading-relaxed text-xs sm:text-sm">
            {formatArticleContent(article.extract)}
          </div>
        </div>

        {/* Read more link */}
        <a
          href={article.fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-[#141beb] hover:text-[#02eec4] font-medium transition-colors flex-shrink-0 text-sm sm:text-base"
        >
          <span className="hidden sm:inline">
            Read full article on Wikipedia
          </span>
          <span className="sm:hidden">Read on Wikipedia</span>
          <svg
            className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      </div>
    </div>
  );
}
